<!--
	/compare/ppf-engine-vs-exactflat — a factual comparison for installers who
	have found ExactFlat while searching for 3D-to-2D pattern software. The hero
	prints both routes from scan to cutter under a real exported cut path; then who each one suits, a row-by-row
	duel, and each tool's strengths on equal footing.
	Rules for this page: every ExactFlat fact is from a public source listed at
	the bottom and dated; where ExactFlat is better, say so; never claim to have
	run software we have only watched. Re-verify `updated` and the sources
	before changing any ExactFlat claim.
-->
<script lang="ts">
	import { resolve } from '$app/paths';
	import { ArticlePage } from '$lib/components/sections';
	import { Reveal, RouteLine, Section, SectionHead } from '$lib/components/ui';
	import type { RouteStep } from '$lib/components/ui';
	import { pricing, usd } from '$lib/content/pricing';
	import Meta from '$lib/seo/Meta.svelte';

	const description =
		'PPF Engine vs ExactFlat for making PPF patterns from 3D scans: what each is built for, what you need to run it, price, availability, and where each is stronger.';

	const exactflatRoute: RouteStep[] = [
		{ name: '3D scan' },
		{ name: 'Rhino 3D', note: 'own licence' },
		{ name: 'ExactFlat plugin' },
		{ name: 'Cutter', note: 'DXF' }
	];
	const ppfengineRoute: RouteStep[] = [
		{ name: '3D scan', note: 'STL · OBJ · PLY' },
		{ name: 'PPF Engine' },
		{ name: 'Cutter', note: 'SVG · DXF · PLT' }
	];

	const fits = [
		{
			name: 'ExactFlat',
			when: 'Look here if',
			points: [
				'You need software this month',
				'You already work in Rhino, or have someone comfortable in CAD',
				'You pattern more than film: upholstery, covers, composites'
			]
		},
		{
			name: 'PPF Engine',
			when: 'Look here if',
			points: [
				'You are a PPF installer without a CAD background',
				'You want seams, reliefs, edge wraps and sensor cutouts handled as PPF decisions',
				'You can wait for delivery, or want to see a demo first'
			],
			ours: true
		}
	] as const;

	const rows = [
		{
			topic: 'Built for',
			exactflat:
				'General 3D-to-2D pattern making: upholstery, marine, apparel, composites, automotive trim. Paint protection film is one listed use.',
			ppfengine: 'One job: PPF patterns made by installers from their own vehicle scans.'
		},
		{
			topic: 'How it runs',
			exactflat: 'A plugin inside Rhino 3D, a general CAD program with its own licence.',
			ppfengine: 'A standalone Windows and macOS desktop app. No CAD program underneath.'
		},
		{
			topic: 'Input',
			exactflat: 'File formats Rhino can open, including scan meshes.',
			ppfengine: 'STL, OBJ or PLY scan meshes.'
		},
		{
			topic: 'Output',
			exactflat: 'DXF for cutters and 2D CAD packages.',
			ppfengine: 'SVG, DXF and PLT (HP-GL), full scale in millimetres.'
		},
		{
			topic: 'PPF-specific tools',
			exactflat:
				'Seams, notches, grain lines and strain analysis for fabric and film in general. Cut-line design relies on Rhino’s CAD tools.',
			ppfengine:
				'Coverage and seam planning on the panel, relief cuts, edge-wrap offsets, measured sensor cutouts, stretch checks against the film’s limit, pre-flight before export.'
		},
		{
			topic: 'Availability',
			exactflat: 'Shipping today, with training and a paid scan-to-pattern service.',
			ppfengine: `Not released yet. In use in our own studio. Presale delivery is estimated ${pricing.deliveryWindow}.`
		}
	] as const;

	const exactflatStrengths = [
		{
			title: 'It exists today',
			body: 'You can buy it, get trained and cut this month.'
		},
		{
			title: 'A very clear flattening loop',
			body: 'Flatten, click the fold, optimise, add the stretch you want, optimise again. Easy to follow.'
		},
		{
			title: 'Target stretch is part of the solve',
			body: 'The stretch you ask for feeds back into the flattening optimisation. PPF Engine keeps the neutral flatten and applies installer compensation as a separate, reversible, measured step. Easier to audit, but ExactFlat’s approach redistributes strain more fully on deep compound panels.'
		},
		{
			title: 'Rhino’s curve tools',
			body: 'If you know Rhino, you have a complete CAD environment for drawing and editing cut lines.'
		}
	] as const;

	const ppfengineStrengths = [
		{
			title: 'A pattern is a design, not a flattened mesh',
			body: 'The flattened surface, the cut line you design, edge wraps and relief cuts are separate things you edit separately. That matters around noisy scan edges and panel returns.'
		},
		{
			title: 'Stretch is judged against the film',
			body: 'Checks report where film has to stretch past its working limit and where excess film would be trapped, before anything is cut.'
		},
		{
			title: 'Openings are measured',
			body: 'A parking sensor is detected from its bezel on the scan, confirmed by you, and cut with a margin you set.'
		},
		{
			title: 'No CAD program to learn or license',
			body: 'One app, from scan to plotter file.'
		},
		{
			title: 'Installers only',
			body: 'Licences are not sold to film manufacturers, and your scans and patterns stay on your machine.'
		}
	] as const;

	const sources = [
		{ label: 'ExactFlat: product overview', href: 'https://www.exactflat.com/' },
		{ label: 'ExactFlat for Rhino 3D', href: 'https://www.exactflat.com/exactflat-for-rhino-3d' },
		{
			label: 'GoMeasure3D: ExactFlat for Rhino listing (price)',
			href: 'https://shop.gomeasure3d.com/products/exactflat-for-rhino'
		}
	] as const;
</script>

<Meta title="PPF Engine vs ExactFlat" {description} />

<ArticlePage
	label="Comparison · 3D-to-2D pattern software"
	title="PPF Engine vs ExactFlat for PPF patterns"
	lede="Both turn a 3D surface into a flat cutting pattern. ExactFlat is a mature, general flattening tool that lives inside Rhino. PPF Engine is a new, standalone app built only for PPF installers. Here is how they differ, including where ExactFlat is the better choice."
	updated="2026-09-19"
	image={{
		asset: 'mbgt-stage-cut',
		w: 2400,
		h: 1424,
		alt: 'The cut file PPF Engine exported for a scanned Mercedes-AMG GT front bumper: one continuous chartreuse outline, ready for the plotter.',
		caption: 'PPF Engine export · one scanned bumper · 775 Bézier segments · unedited draft'
	}}
>
	{#snippet routes()}
		<RouteLine label="ExactFlat" steps={exactflatRoute} />
		<RouteLine label="PPF Engine" steps={ppfengineRoute} live />
	{/snippet}

	<!-- ── Who each one suits ───────────────────────────────────────────── -->
	<Section id="fit">
		<SectionHead
			index="01"
			label="The short version"
			heading="Which one fits your shop?"
			intro="These are different tools for different shops. An honest answer saves you a purchase you regret."
		/>
		<div class="fits">
			{#each fits as fit, i (fit.name)}
				<Reveal class="fit {'ours' in fit ? 'fit--ours' : ''}" delay={i * 80}>
					<p class="fit__when">{fit.when}</p>
					<h3>{fit.name}</h3>
					<ul>
						{#each fit.points as point (point)}
							<li>{point}</li>
						{/each}
					</ul>
				</Reveal>
			{/each}
		</div>
	</Section>

	<!-- ── The duel ─────────────────────────────────────────────────────── -->
	<Section id="facts" tone="panel">
		<SectionHead index="02" label="Side by side" heading="The facts, line by line." />

		<div class="price">
			<Reveal class="price__cell">
				<p class="price__who">ExactFlat for Rhino</p>
				<p class="price__num">USD 6,995</p>
				<p class="price__note">
					Full seat, as listed by a US reseller on the date above. Rhino is licensed separately.
					Confirm current pricing with ExactFlat or a reseller.
				</p>
			</Reveal>
			<Reveal class="price__cell price__cell--ours" delay={80}>
				<p class="price__who">PPF Engine</p>
				<p class="price__num">{usd(pricing.founderYear)}<span> first year, presale</span></p>
				<p class="price__note">
					Renewal locked at {usd(pricing.founderRenewal)} per year. Public launch price
					{usd(pricing.annual)} per year. Refundable until your access begins.
				</p>
			</Reveal>
		</div>

		<div class="duel" role="table" aria-label="ExactFlat and PPF Engine compared">
			<div class="duel__head" role="row">
				<span role="columnheader">Topic</span>
				<span role="columnheader">ExactFlat</span>
				<span role="columnheader" class="duel__ours">PPF Engine</span>
			</div>
			{#each rows as row (row.topic)}
				<div class="duel__row" role="row">
					<div class="duel__topic" role="rowheader">{row.topic}</div>
					<div role="cell" data-who="ExactFlat">{row.exactflat}</div>
					<div role="cell" data-who="PPF Engine" class="duel__ours">{row.ppfengine}</div>
				</div>
			{/each}
		</div>
	</Section>

	<!-- ── Strengths, on equal footing ──────────────────────────────────── -->
	<Section id="strengths" tone="dark">
		<SectionHead
			index="03"
			label="Credit where due"
			heading="Where each one is stronger."
			intro="We studied ExactFlat’s public demonstrations closely while building PPF Engine. We have not run a licensed copy, so the left column is our reading of what ExactFlat shows publicly."
		/>
		<div class="strengths">
			<div>
				<p class="strengths__who">ExactFlat</p>
				<ol>
					{#each exactflatStrengths as s, i (s.title)}
						<Reveal as="li" delay={i * 50}>
							<h3>{s.title}</h3>
							<p>{s.body}</p>
						</Reveal>
					{/each}
				</ol>
			</div>
			<div class="strengths__ours">
				<p class="strengths__who">PPF Engine</p>
				<ol>
					{#each ppfengineStrengths as s, i (s.title)}
						<Reveal as="li" delay={i * 50}>
							<h3>{s.title}</h3>
							<p>{s.body}</p>
						</Reveal>
					{/each}
				</ol>
			</div>
		</div>
		<p class="both">
			Neither tool removes the physical test fit. Whatever you flatten with, cut a test piece and
			check it on the car before production film.
		</p>
	</Section>

	<!-- ── Sources ──────────────────────────────────────────────────────── -->
	<Section id="sources">
		<div class="sources">
			<SectionHead index="04" label="Sources" heading="Where the ExactFlat facts come from." />
			<div>
				<ul>
					{#each sources as source (source.href)}
						<li><a href={source.href} rel="noopener external">{source.label}</a></li>
					{/each}
				</ul>
				<p>
					ExactFlat is a trademark of its owner. PPF Engine is not affiliated with ExactFlat. If
					anything here is out of date or wrong, email hello@ppfengine.com and we will correct it.
				</p>
				<p>
					Related: <a href={resolve('/technology/scanner-plotter-compatibility')}
						>scanner and plotter compatibility</a
					>
					and
					<a href={resolve('/technology/3d-scan-to-ppf-pattern')}>how a scan becomes a pattern</a>.
				</p>
			</div>
		</div>
	</Section>
</ArticlePage>

<style>
	/* ── Fit cards ──────────────────────────────────────────────────────── */
	.fits {
		display: grid;
		gap: 1rem;
		margin-top: clamp(2.5rem, 5vw, 4rem);
	}
	@media (min-width: 800px) {
		.fits {
			grid-template-columns: repeat(2, minmax(0, 1fr));
		}
	}
	.fits :global(.fit) {
		padding: clamp(1.5rem, 3vw, 2.5rem);
		border: 1px solid var(--color-line-strong);
		border-radius: var(--radius-md);
		background: var(--color-surface);
	}
	.fits :global(.fit--ours) {
		border-color: var(--color-app-950);
		background: var(--color-app-950);
		color: var(--color-bone-300);
	}
	.fit__when {
		font-family: var(--font-mono);
		font-size: var(--text-mono-s);
		letter-spacing: 0.08em;
		text-transform: uppercase;
		color: var(--color-ink-400);
	}
	.fits h3 {
		margin-top: 0.5rem;
		font-size: clamp(1.75rem, 3vw, 2.5rem);
		font-weight: 600;
		letter-spacing: -0.03em;
		line-height: 1.05;
	}
	.fits :global(.fit--ours h3) {
		color: var(--color-cut-400);
	}
	.fits :global(.fit--ours .fit__when) {
		color: var(--color-bone-500);
	}
	.fits ul {
		list-style: none;
		margin-top: 1.75rem;
		border-top: 1px solid var(--color-line);
	}
	.fits :global(.fit--ours ul),
	.fits :global(.fit--ours li) {
		border-color: var(--color-app-700);
	}
	.fits li {
		position: relative;
		padding: 0.875rem 0 0.875rem 1.5rem;
		border-bottom: 1px solid var(--color-line);
		text-wrap: pretty;
	}
	.fits li::before {
		content: '';
		position: absolute;
		left: 0;
		top: 1.375rem;
		width: 6px;
		height: 6px;
		background: var(--color-ink-300);
	}
	.fits :global(.fit--ours li::before) {
		background: var(--color-cut-400);
	}

	/* ── Price ──────────────────────────────────────────────────────────── */
	.price {
		display: grid;
		gap: 1px;
		margin-top: clamp(2.5rem, 5vw, 4rem);
		border: 1px solid var(--color-line-strong);
		background: var(--color-line-strong);
	}
	@media (min-width: 800px) {
		.price {
			grid-template-columns: repeat(2, minmax(0, 1fr));
		}
	}
	.price :global(.price__cell) {
		padding: clamp(1.5rem, 3vw, 2.5rem);
		background: var(--color-surface);
	}
	.price :global(.price__cell--ours) {
		background: var(--color-cut-100);
	}
	.price__who {
		font-family: var(--font-mono);
		font-size: var(--text-mono-s);
		letter-spacing: 0.08em;
		text-transform: uppercase;
		color: var(--color-ink-500);
	}
	.price__num {
		margin-top: 0.75rem;
		font-size: clamp(2.5rem, 5vw, 4rem);
		font-weight: 600;
		line-height: 1;
		letter-spacing: -0.04em;
		color: var(--color-ink-900);
	}
	.price__num span {
		font-size: 1rem;
		font-weight: 400;
		letter-spacing: 0;
		color: var(--color-ink-500);
	}
	.price__note {
		max-width: 44ch;
		margin-top: 1rem;
		color: var(--color-ink-500);
		text-wrap: pretty;
	}

	/* ── Duel ───────────────────────────────────────────────────────────── */
	.duel {
		margin-top: clamp(2rem, 4vw, 3rem);
	}
	.duel__head {
		display: none;
	}
	.duel__row {
		display: grid;
		gap: 0.75rem;
		padding-block: 1.5rem;
		border-top: 1px solid var(--color-line-strong);
	}
	.duel__topic {
		font-family: var(--font-mono);
		font-size: var(--text-mono-s);
		letter-spacing: 0.08em;
		text-transform: uppercase;
		color: var(--color-cut-700);
	}
	.duel__row [role='cell'] {
		color: var(--color-ink-700);
		text-wrap: pretty;
	}
	.duel__row [role='cell']::before {
		content: attr(data-who);
		display: block;
		margin-bottom: 0.25rem;
		font-family: var(--font-mono);
		font-size: 0.6875rem;
		letter-spacing: 0.08em;
		text-transform: uppercase;
		color: var(--color-ink-400);
	}
	.duel__row .duel__ours {
		color: var(--color-ink-900);
	}
	@media (min-width: 800px) {
		.duel__head,
		.duel__row {
			display: grid;
			grid-template-columns: minmax(9rem, 2fr) minmax(0, 5fr) minmax(0, 5fr);
			gap: clamp(1.5rem, 3vw, 3rem);
		}
		.duel__head {
			position: sticky;
			top: 4.5rem;
			z-index: 1;
			padding-block: 0.875rem;
			background: var(--color-panel);
			font-family: var(--font-mono);
			font-size: var(--text-mono-s);
			letter-spacing: 0.08em;
			text-transform: uppercase;
			color: var(--color-ink-400);
		}
		.duel__head .duel__ours {
			color: var(--color-cut-700);
		}
		.duel__row [role='cell']::before {
			display: none;
		}
	}

	/* ── Strengths ──────────────────────────────────────────────────────── */
	.strengths {
		display: grid;
		gap: clamp(2.5rem, 5vw, 5rem);
		margin-top: clamp(2.5rem, 5vw, 4rem);
	}
	@media (min-width: 900px) {
		.strengths {
			grid-template-columns: repeat(2, minmax(0, 1fr));
		}
	}
	.strengths__who {
		font-size: 1.5rem;
		font-weight: 600;
		letter-spacing: -0.02em;
		color: var(--color-bone-100);
	}
	.strengths__ours .strengths__who {
		color: var(--color-cut-400);
	}
	.strengths ol {
		list-style: none;
		margin-top: 1.25rem;
		counter-reset: s;
	}
	.strengths :global(li) {
		counter-increment: s;
		display: grid;
		grid-template-columns: 2.5rem minmax(0, 1fr);
		padding-block: 1.25rem;
		border-top: 1px solid var(--color-app-700);
	}
	.strengths :global(li)::before {
		content: counter(s, decimal-leading-zero);
		grid-row: span 2;
		font-family: var(--font-mono);
		font-size: var(--text-mono-s);
		letter-spacing: 0.06em;
		color: var(--color-bone-500);
	}
	.strengths__ours :global(li)::before {
		color: var(--color-cut-400);
	}
	.strengths h3 {
		font-weight: 600;
		letter-spacing: -0.01em;
		color: var(--color-bone-100);
	}
	.strengths :global(li p) {
		grid-column: 2;
		margin-top: 0.375rem;
		color: var(--color-bone-500);
		text-wrap: pretty;
	}
	.both {
		max-width: 62ch;
		margin-top: clamp(2.5rem, 4vw, 3.5rem);
		padding-left: 1rem;
		border-left: 2px solid var(--color-cut-400);
		color: var(--color-bone-300);
	}

	/* ── Sources ────────────────────────────────────────────────────────── */
	.sources {
		display: grid;
		gap: clamp(2rem, 5vw, 5rem);
	}
	@media (min-width: 900px) {
		.sources {
			grid-template-columns: minmax(0, 5fr) minmax(0, 6fr);
		}
	}
	.sources ul {
		list-style: none;
		border-top: 1px solid var(--color-line-strong);
	}
	.sources li {
		padding-block: 0.875rem;
		border-bottom: 1px solid var(--color-line);
	}
	.sources a {
		color: var(--color-ink-900);
		text-decoration: underline;
		text-underline-offset: 0.2em;
	}
	.sources p {
		max-width: 60ch;
		margin-top: 1.25rem;
		color: var(--color-ink-500);
	}
</style>
